import styled from '@emotion/styled'
import { GamNetworkConfig } from '@pubstack/common/src/gam/adx'
import { FunctionComponent, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import Button from '~/components/Button'
import { CodeSnippet } from '~/components/CodeSnippet'
import { Input } from '~/components/Input'
import { Link } from '~/components/Link'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Message } from '~/components/Message'
import { Modal } from '~/components/Modal'
import { Select } from '~/components/Select'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'

const GamManagerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ModalMessage = styled(Message)`
  margin-bottom: 20px;
`

const InputContainer = styled.div`
  height: 70px;
`

const ValidateActionModal: FunctionComponent<{
  runCreationProcess: (network: GamNetworkConfig) => void
  gamNetwork: GamNetworkConfig
}> = ({ runCreationProcess, gamNetwork }) => {
  const modal = useGlobalModal()

  const { control, reset } = useForm<{
    network: string
  }>()
  const network = useWatch({ control, name: 'network' })

  const close = () => {
    reset()
    modal.close()
  }

  const click = () => {
    runCreationProcess(gamNetwork)
    close()
  }

  return (
    <Modal.Content>
      <Modal.Title>GAM object creation</Modal.Title>

      <Modal.Body>
        <ModalMessage
          status={'warning'}
          title={'Warning'}
          description={
            <Wrapper>
              <span>
                You are about to configure the GAM account of {gamNetwork.id} ({gamNetwork.networkCode}) .
              </span>
              <span>Please enter the network Id {gamNetwork.id} to validate the operation</span>
            </Wrapper>
          }
        />
        <InputContainer>
          <Input type={'text'} control={control} name={'network'} label={'networkId'} required={true} />
        </InputContainer>
      </Modal.Body>

      <Modal.Actions>
        <Button variant={'tertiary'} onClick={close} trackingLabel={'adm-admin-scopes-detail-gam-configuration-cancel'}>
          Cancel
        </Button>
        <Button variant={'negative'} disabled={network !== gamNetwork.id} onClick={click} trackingLabel={'adm-admin-scopes-detail-gam-configuration-confirm'}>
          Validate operation
        </Button>
      </Modal.Actions>
    </Modal.Content>
  )
}

type PureGamManagerProps = {
  networks: GamNetworkConfig[]
  runCreationProcess: (network: GamNetworkConfig) => void
  onNetworkChange: (network: GamNetworkConfig) => unknown
  isRunning: boolean
  isLoading: boolean
  state: GamManagerStateToDisplay | undefined
}

export type GamManagerStateToDisplay = {
  creativeNames: string[]
  orderNames: string[]
  lineItemCount: number
  associations: number
}

const UnstyledPureGamManager: FunctionComponent<PureGamManagerProps> = ({ networks, runCreationProcess, onNetworkChange, isRunning, isLoading, state }) => {
  const modal = useGlobalModal()

  const { control, handleSubmit } = useForm<{
    network: string
  }>()

  const siteOptions: SelectOptionProp<string>[] = networks.map((s) => ({
    label: `${s.id} (${s.networkCode})`,
    value: s.id,
    trackingLabel: 'adm-admin-scopes-detail-gam-configuration-network-set',
  }))

  const [hasSelect, setHasSelect] = useState(false)

  return (
    <Wrapper>
      <Message
        status={'warning'}
        title={'Warning'}
        description={
          <Wrapper>
            <span>You are about to configure the GAM account of a customer.</span>
            <span>Be wary that all operations cannot be undone.</span>
            <span>Double check if you&#39;re configuring the right network.</span>
            <span>Read the documentation before performing the operation.</span>
            <span>
              <Link
                label={'GAM ADM Setup'}
                href={'https://www.notion.so/GAM-Adm-Setup-via-Autobox-9de3eed1fa0a458198a81e7629c287cf'}
                trackingLabel={'adm-admin-scopes-detail-gam-configuration-doc-navigate'}
              />
            </span>
          </Wrapper>
        }
      />

      <GamManagerActions>
        <Select
          searchable
          control={control}
          name={'network'}
          label={'Network'}
          options={siteOptions}
          onChange={(option: SelectOptionProp) => {
            const networkId = option.value
            const gamNetwork = networks.find((n) => n.id === networkId)
            if (gamNetwork) {
              setHasSelect(true)
              onNetworkChange(gamNetwork)
            }
          }}
        />
        <Button
          disabled={isLoading || isRunning || !hasSelect}
          iconName={isLoading ? 'loading' : undefined}
          iconSpin={isLoading}
          onClick={handleSubmit((data) => {
            const { network } = data
            const gamNetwork = networks.find((n) => n.id === network)
            if (gamNetwork) {
              modal.open(ValidateActionModal, { runCreationProcess, gamNetwork })
            }
          })}
          trackingLabel={'adm-admin-scopes-detail-gam-configuration-apply'}
        >
          Configure GAM account
        </Button>
        <span>{isRunning ? `A job is already running for the selected network` : ''}</span>
      </GamManagerActions>

      <Wrapper>
        <CodeSnippet trackingLabel="admin-gamManager-view" contentText={JSON.stringify(state ?? {}, null, 2)} />
      </Wrapper>
    </Wrapper>
  )
}

export const PureGamManager = styled(UnstyledPureGamManager)``
